<template>
  <div>
    <slot />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import shortcutMixin, { ShortcutConfig } from '@shortcuts/shortcutMixin';

export type OverlayCloseEvent = {
  reason: 'outsideClick' | 'escapeKey';
  event: KeyboardEvent | MouseEvent;
};

/**
 * Wraps any content and adds additional functionality related to closing overlays:
 *
 * - detects outside click
 * - detects escape press
 *
 * @emits 'close' When the overlay should be closed. The component itself does not have
 * any logic for closing / opening
 */
export default defineComponent({
  mixins: [shortcutMixin],
  name: 'Overlay',
  emits: { close: null },

  props: {
    closeOnEsc: {
      type: Boolean,
      required: false,
      default: true,
    },

    closeOnOutsideClick: {
      type: Boolean,
      required: false,
      default: true,
    },

    opener: {
      type: HTMLElement as PropType<HTMLElement>,
      required: false,
      default: null,
    },

    name: {
      type: String,
      required: false,
      default: 'overlay',
    },
  },

  shortcuts(): ShortcutConfig {
    return this.closeOnEsc
      ? {
          cancel: (event) => {
            this.close('escapeKey', event);
          },
        }
      : {};
  },

  data(): { unsubscribe?: any; shortcutsBound?: boolean; modalsOnTopOfOverlay: Set<string> } {
    return {
      modalsOnTopOfOverlay: new Set(),
    };
  },

  methods: {
    close(reason: 'outsideClick' | 'escapeKey', event: KeyboardEvent | MouseEvent) {
      this.$emit('close', { reason, event });
    },

    inContainer(target: HTMLElement) {
      return target === this.$el ? false : this.$el.contains(target);
    },

    inOpener(target: HTMLElement) {
      if (!this.opener) return false;

      return target === this.opener || this.opener.contains(target);
    },

    isWhitelisted(target: HTMLElement) {
      if (
        ('overlayWhitelist' in target.dataset && target.dataset.overlayWhitelist.includes(this.name)) ||
        target.closest(`[data-overlay-whitelist*="${this.name}"]`)
      ) {
        return true;
      }

      return false;
    },

    handleAppClick(event: MouseEvent) {
      const target = event.target as HTMLElement;

      // Don't close if clicking
      //  - on modal that is on top of the overlay
      //  - on the overlay
      //  - on the element that opens the overlay
      //  - on an element that has been whitelisted explicitly
      if (
        this.modalsOnTopOfOverlay.size > 0 ||
        this.inContainer(target) ||
        this.inOpener(target) ||
        this.isWhitelisted(target)
      ) {
        return;
      }

      // Manually remove the shortcuts. Otherwise, if a document click opens a new overlay,
      // the current overlay is destroyed after the new one opens, which will remove the wrong
      // shortcut handler
      if (this.shortcutsBound) {
        this._unbindShortcuts();
        this.shortcutsBound = false;
      }

      this.close('outsideClick', event);
    },
  },

  mounted() {
    if (this.closeOnOutsideClick) {
      // Support: Chrome
      // Without setTimeout, this will fire when a mouseup opens an overlay, immediately closing
      // the overlay again
      setTimeout(() => {
        document.querySelector('body').addEventListener('click', this.handleAppClick, true);
      });
    }

    /**
     * Subscribe to modals opening. If a modal is opened on top of
     * the overlay, ignore clicks outside that would otherwise close the modal
     */
    this.unsubscribe = this.$store.subscribe((mutation) => {
      if (mutation.type === 'modal/MODAL_SHOW') {
        this.modalsOnTopOfOverlay.add(mutation.payload.type);
      } else if (mutation.type === 'modal/MODAL_HIDE') {
        this.modalsOnTopOfOverlay.delete(mutation.payload.type);
      }
    });
  },

  unmounted() {
    this.unsubscribe();
  },

  beforeUnmount() {
    if (this.closeOnOutsideClick) {
      // The listener removal must also be in a setTimeout, so that when the component is mounted and
      // then immediately destroyed, the listener is removed
      setTimeout(() => {
        document.querySelector('body').removeEventListener('click', this.handleAppClick, true);
      });
    }
  },
});
</script>
